import React, { useState, useEffect, Suspense, lazy } from 'react';
import { MapPin, Bell, FileText, Calendar, Home, Menu, X, Mail, Coffee, ArrowRight } from 'lucide-react';
import { differenceInDays, parseISO, format as formatDateFns } from 'date-fns';
import { CountdownTimer } from './components/CountdownTimer';
import { MapleLeaf } from './components/icons/MapleLeaf';
import { useAppState } from './hooks/useLocalStorage';
import { calculateCitizenshipEligibility } from './utils/residencyCalculator';
import { Trip, NotificationPreference, QuestionnaireResponse, CitizenshipEligibility, TemporaryResidencyPeriod } from './types';
import { generateAccessToken, getTokenFromUrl } from './utils/tokenUtils';
import './styles/design-system.css';

const TripTracker = lazy(() => import('./components/TripTracker').then(m => ({ default: m.TripTracker })));
const Questionnaire = lazy(() => import('./components/Questionnaire').then(m => ({ default: m.Questionnaire })));
const NotificationSettings = lazy(() => import('./components/NotificationSettings').then(m => ({ default: m.NotificationSettings })));
const EmailManager = lazy(() => import('./components/EmailManager').then(m => ({ default: m.EmailManager })));

type TabType = 'dashboard' | 'trips' | 'questionnaire' | 'notifications';

function App() {
  const {
    user,
    setUser,
    trips,
    setTrips,
    temporaryPeriods,
    setTemporaryPeriods,
    notificationPreferences,
    setNotificationPreferences,
    questionnaireResponse,
    setQuestionnaireResponse
  } = useAppState();

  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [eligibility, setEligibility] = useState<CitizenshipEligibility | null>(null);
  const [questionnaireResponses, setQuestionnaireResponses] = useState<Record<string, any>>({});
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);
  const [recentImpact, setRecentImpact] = useState<{ daysRemainingDelta: number; daysCompletedDelta: number } | null>(null);
  const [isLoadingFromToken, setIsLoadingFromToken] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);

  // Check for token in URL and load data
  useEffect(() => {
    const loadDataFromToken = async () => {
      const token = getTokenFromUrl();
      if (token) {
        setIsLoadingFromToken(true);
        try {
          const response = await fetch(`/.netlify/functions/get-user-data?token=${token}`);
          const data = await response.json();
          
          if (response.ok && data.success && data.userData) {
            // Load user data from backend
            if (data.userData.user) setUser(data.userData.user);
            if (data.userData.trips) setTrips(data.userData.trips);
            if (data.userData.temporaryPeriods) setTemporaryPeriods(data.userData.temporaryPeriods);
            if (data.userData.notificationPreferences) setNotificationPreferences(data.userData.notificationPreferences);
            if (data.userData.questionnaireResponse) setQuestionnaireResponse(data.userData.questionnaireResponse);
          }
        } catch (error) {
          console.error('Error loading data from token:', error);
        } finally {
          setIsLoadingFromToken(false);
        }
      }
    };

    loadDataFromToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize user if not exists
  useEffect(() => {
    if (!user) {
      const defaultUser = {
        id: `user-${Date.now()}`,
        name: 'User',
        prDate: '',
        birthDate: '',
        accessToken: generateAccessToken(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setUser(defaultUser);
    } else if (!user.accessToken) {
      // Add token to existing user
      setUser({
        ...user,
        accessToken: generateAccessToken(),
        updatedAt: new Date().toISOString()
      });
    }
  }, [user, setUser]);

  // Canadian light (maple) theme
  useEffect(() => {
    document.documentElement.removeAttribute('data-theme');
    document.documentElement.style.colorScheme = 'light';
  }, []);

  // Small screen detection
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsSmallScreen(mq.matches);
    handler();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Prefetch likely next tab
  useEffect(() => {
    if (activeTab === 'dashboard') {
      import('./components/Questionnaire');
      import('./components/TripTracker');
    } else if (activeTab === 'questionnaire') {
      import('./components/TripTracker');
    } else if (activeTab === 'trips') {
      import('./components/NotificationSettings');
    }
  }, [activeTab]);

  // Initialize notification preferences if not exists
  useEffect(() => {
    if (!notificationPreferences) {
      const defaultPreferences: NotificationPreference = {
        id: 'pref-1',
        userId: 'user-1',
        enabled: true,
        reminderIntervals: ['3_months', '6_months'],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setNotificationPreferences(defaultPreferences);
    }
  }, [notificationPreferences, setNotificationPreferences]);

  // Load questionnaire responses
  useEffect(() => {
    if (questionnaireResponse) {
      setQuestionnaireResponses(questionnaireResponse.responses);
    }
  }, [questionnaireResponse]);

  // Calculate eligibility whenever relevant data changes
  useEffect(() => {
    if (user?.prDate) {
      const calculatedEligibility = calculateCitizenshipEligibility(
        user.prDate,
        trips,
        temporaryPeriods
      );
      setEligibility(calculatedEligibility);
    }
  }, [user?.prDate, trips, temporaryPeriods]);

  // Trip management functions
  const handleAddTrip = (tripData: Omit<Trip, 'id' | 'userId' | 'createdAt'>) => {
    const newTrip: Trip = {
      ...tripData,
      id: `trip-${Date.now()}`,
      userId: user?.id || 'user-1',
      createdAt: new Date().toISOString()
    };
    const before = eligibility;
    const newTrips = [...trips, newTrip];
    setTrips(newTrips);
    if (user?.prDate) {
      const after = calculateCitizenshipEligibility(user.prDate, newTrips, temporaryPeriods);
      if (before) {
        setRecentImpact({
          daysRemainingDelta: after.daysRemaining - before.daysRemaining,
          daysCompletedDelta: after.daysCompleted - before.daysCompleted
        });
      }
    }
  };

  const handleUpdateTrip = (tripId: string, updates: Partial<Trip>) => {
    setTrips(trips.map(trip => 
      trip.id === tripId ? { ...trip, ...updates } : trip
    ));
  };

  const handleDeleteTrip = (tripId: string) => {
    setTrips(trips.filter(trip => trip.id !== tripId));
  };

  // Questionnaire functions
  const handleUpdateQuestionnaireResponses = (responses: Record<string, any>) => {
    setQuestionnaireResponses(responses);
  };

  const handleCompleteQuestionnaire = (responses: Record<string, any>) => {
    const questionnaireData: QuestionnaireResponse = {
      id: `questionnaire-${Date.now()}`,
      userId: user?.id || 'user-1',
      responses,
      completedAt: new Date().toISOString(),
      version: '1.0'
    };
    setQuestionnaireResponse(questionnaireData);
    
    // Extract PR date from questionnaire if user doesn't have it
    if (responses.pr_date && user && !user.prDate) {
      setUser({
        ...user,
        prDate: responses.pr_date,
        updatedAt: new Date().toISOString()
      });
    }

    // Derive temporary residency periods from responses
    if (responses.pr_date) {
      const prDateStr: string = responses.pr_date;
      const derivedPeriods: TemporaryResidencyPeriod[] = [];

      if (responses.had_temporary_status) {
        if (responses.student_start_date) {
          const start = responses.student_start_date as string;
          const end = prDateStr;
          const days = Math.max(0, differenceInDays(parseISO(end), parseISO(start)));
          derivedPeriods.push({
            id: `tmp-student-${Date.now()}`,
            userId: user?.id || 'user-1',
            startDate: start,
            endDate: end,
            status: 'student',
            daysInCanada: days,
            createdAt: new Date().toISOString()
          });
        }
        if (responses.worker_start_date) {
          const start = responses.worker_start_date as string;
          const end = prDateStr;
          const days = Math.max(0, differenceInDays(parseISO(end), parseISO(start)));
          derivedPeriods.push({
            id: `tmp-worker-${Date.now()}`,
            userId: user?.id || 'user-1',
            startDate: start,
            endDate: end,
            status: 'worker',
            daysInCanada: days,
            createdAt: new Date().toISOString()
          });
        }
      }

      if (derivedPeriods.length > 0) {
        setTemporaryPeriods([...(temporaryPeriods || []), ...derivedPeriods]);
      }
    }
    
    // Better flow: If user has trips, go to Trips tab first, then email modal
    const userWantsToAddTrips = responses.has_trips === true;
    
    if (userWantsToAddTrips && trips.length === 0) {
      // User wants to add trips - go to Trips tab first
      setActiveTab('trips');
      // Don't show email modal yet - will show after they add trips
    } else if (!user?.email) {
      // No trips to add (or already added) - show email modal
      setShowEmailCapture(true);
    } else {
      // Already has email and no trips needed - go to dashboard
      setActiveTab('dashboard');
    }
  };

  // Notification preferences functions
  const handleUpdateNotificationPreferences = (updates: Partial<NotificationPreference>) => {
    if (notificationPreferences) {
      setNotificationPreferences({
        ...notificationPreferences,
        ...updates,
        updatedAt: new Date().toISOString()
      });
    }
  };

  // Email management functions
  const handleEmailSaved = (email: string, reminderIntervals?: any[]) => {
    if (user) {
      setUser({
        ...user,
        email,
        updatedAt: new Date().toISOString()
      });
      
      // Update notification preferences with reminder intervals
      if (reminderIntervals && reminderIntervals.length > 0) {
        const updatedPreferences: NotificationPreference = {
          id: notificationPreferences?.id || `pref-${Date.now()}`,
          userId: user.id,
          enabled: true,
          email,
          reminderIntervals: reminderIntervals as any[],
          createdAt: notificationPreferences?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        setNotificationPreferences(updatedPreferences);
      }
      
      // Save data to backend
      saveDataToBackend();
      
      // Close email capture modal and go to dashboard
      setShowEmailCapture(false);
      setActiveTab('dashboard');
    }
  };

  const handleSkipEmail = () => {
    setShowEmailCapture(false);
    setActiveTab('dashboard');
  };

  const saveDataToBackend = async () => {
    if (!user?.accessToken) return;
    
    try {
      await fetch('/.netlify/functions/save-user-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: user.accessToken,
          userData: {
            user,
            trips,
            temporaryPeriods,
            notificationPreferences,
            questionnaireResponse
          }
        })
      });
    } catch (error) {
      console.error('Error saving data to backend:', error);
    }
  };

  // Auto-save data when it changes
  useEffect(() => {
    if (user?.accessToken && user?.email) {
      saveDataToBackend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trips, temporaryPeriods, notificationPreferences, questionnaireResponse]);

  const tabs = [
    { id: 'dashboard' as TabType, label: 'Dashboard', icon: Home },
    { id: 'trips' as TabType, label: 'Trips', icon: MapPin },
    { id: 'questionnaire' as TabType, label: 'Assessment', icon: FileText },
    { id: 'notifications' as TabType, label: 'Notifications', icon: Bell }
  ];

  if (isLoadingFromToken) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--sd-color-bg-page)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: 'var(--sd-font-size-lg)',
            color: 'var(--sd-color-text-secondary)',
            marginBottom: 'var(--sd-space-4)'
          }}>
            Loading your countdown data...
          </div>
        </div>
      </div>
    );
  }

  const needsSetup = !user?.prDate || !questionnaireResponse;

  return (
    <div className={`app-shell${questionnaireResponse ? ' has-fab' : ''}`}>
      {/* Email Capture Modal - Appears after questionnaire completion */}
      {showEmailCapture && (
        <>
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--sd-space-4)',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}>
            <div style={{
              maxWidth: '500px',
              width: '100%',
              maxHeight: 'min(100%, calc(100dvh - 2rem))',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              backgroundColor: 'var(--sd-color-bg-container)',
              borderRadius: 'var(--sd-radius-xl)',
              padding: 'var(--sd-space-6)',
              border: '1px solid var(--sd-color-border-subtle)',
              boxShadow: 'var(--sd-shadow-lg)',
              margin: 'auto'
            }}>
              <div style={{ textAlign: 'center', marginBottom: 'var(--sd-space-5)' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  margin: '0 auto var(--sd-space-4) auto',
                  background: 'linear-gradient(135deg, var(--sd-color-brand-primary), var(--sd-color-brand-secondary))',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Mail size={32} style={{ color: 'white' }} />
                </div>
                <h2 style={{
                  margin: '0 0 var(--sd-space-2) 0',
                  fontSize: 'var(--sd-font-size-display-sm)',
                  fontWeight: 'var(--sd-font-weight-bold)',
                  color: 'var(--sd-color-text-primary)'
                }}>
                  Great! You're All Set
                </h2>
                <p style={{
                  margin: 0,
                  fontSize: 'var(--sd-font-size-md)',
                  color: 'var(--sd-color-text-secondary)'
                }}>
                  Want to access your countdown from any device? Get a secure link sent to your email.
                </p>
              </div>

              <Suspense fallback={<div>Loading...</div>}>
                <EmailManager
                  userEmail={user?.email}
                  userToken={user?.accessToken}
                  onEmailSaved={handleEmailSaved}
                />
              </Suspense>

              <button
                onClick={handleSkipEmail}
                style={{
                  width: '100%',
                  marginTop: 'var(--sd-space-3)',
                  padding: 'var(--sd-space-3)',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--sd-color-text-tertiary)',
                  cursor: 'pointer',
                  fontSize: 'var(--sd-font-size-sm)',
                  transition: 'color 0.2s',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
                onMouseOver={(e) => e.currentTarget.style.color = 'var(--sd-color-text-secondary)'}
                onMouseOut={(e) => e.currentTarget.style.color = 'var(--sd-color-text-tertiary)'}
              >
                Skip for now
                <ArrowRight size={14} aria-hidden="true" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Header */}
      {!(activeTab === 'questionnaire' && isSmallScreen) && (
      <header
        className={activeTab === 'questionnaire' ? 'mobile-condense' : undefined}
        style={{
          background: 'linear-gradient(180deg, rgba(213, 43, 30, 0.08), var(--sd-color-bg-container))',
          borderBottom: '1px solid var(--sd-color-border-subtle)',
          boxShadow: 'var(--sd-shadow-xs)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backdropFilter: 'blur(10px)',
          width: '100%'
        }}
      >
        <div style={{
          minHeight: 'var(--sd-header-height)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--sd-space-3)',
          paddingTop: 'var(--sd-space-3)',
          paddingBottom: 'var(--sd-space-3)',
          paddingLeft: 'max(0px, env(safe-area-inset-left, 0px))',
          paddingRight: 'max(12px, env(safe-area-inset-right, 0px))',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div
            className="flex"
            style={{
              gap: 'var(--sd-space-3)',
              alignItems: 'center',
              minWidth: 0,
              paddingLeft: 0
            }}
          >
            <button
              type="button"
              onClick={() => window.location.reload()}
              aria-label="Refresh page"
              title="Refresh page"
              style={{
                width: '40px',
                height: '40px',
                flexShrink: 0,
                backgroundColor: 'var(--sd-color-brand-primary)',
                borderRadius: 'var(--sd-radius-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                marginLeft: 0
              }}
            >
              <Calendar size={24} style={{ color: 'var(--sd-color-text-inverse)' }} />
            </button>
            <div style={{ minWidth: 0, textAlign: 'left' }}>
              <h1 style={{
                margin: 0,
                fontSize: 'var(--sd-font-size-lg)',
                fontWeight: 'var(--sd-font-weight-bold)',
                color: 'var(--sd-color-text-primary)',
                lineHeight: 1.2
              }}>
                Citizenship Countdown
              </h1>
              <p className="hidden-mobile" style={{
                margin: 0,
                fontSize: 'var(--sd-font-size-sm)',
                color: 'var(--sd-color-text-secondary)',
                lineHeight: 1.3
              }}>
                Track your path to Canadian citizenship
              </p>
            </div>
          </div>

          <button
            className="hidden-desktop"
            aria-label="Open menu"
            onClick={() => setIsMobileMenuOpen(true)}
            style={{
              background: 'transparent',
              border: '1px solid var(--sd-color-border-subtle)',
              borderRadius: 'var(--sd-radius-md)',
              padding: '8px',
              color: 'var(--sd-color-text-secondary)',
              flexShrink: 0
            }}
          >
            <Menu size={20} />
          </button>
        </div>
      </header>
      )}

      {/* Navigation */}
      {! (activeTab === 'questionnaire' && isSmallScreen) && (<nav className="hidden-desktop" style={{ display: 'none' }} />)}
      {! (activeTab === 'questionnaire' && isSmallScreen) && (<nav className="hidden-mobile" style={{
        backgroundColor: 'var(--sd-color-bg-container)',
        borderBottom: '1px solid var(--sd-color-border-subtle)'
      }}>
        <div className="container">
          <div style={{
            display: 'flex',
            gap: 'var(--sd-space-2)',
            padding: 'var(--sd-space-3) 0',
            flexWrap: 'wrap'
          }}>
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sd-space-2)',
                    padding: 'var(--sd-space-2) var(--sd-space-4)',
                    border: 'none',
                    borderRadius: 'var(--sd-radius-pill)',
                    background: isActive 
                      ? 'linear-gradient(90deg, var(--sd-color-brand-primary), var(--sd-color-brand-secondary))' 
                      : 'transparent',
                    color: isActive 
                      ? 'var(--sd-color-text-inverse)' 
                      : 'var(--sd-color-text-secondary)',
                    fontSize: 'var(--sd-font-size-sm)',
                    fontWeight: 'var(--sd-font-weight-medium)',
                    cursor: 'pointer',
                    transition: 'all 0.18s ease'
                  }}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </nav>)}

      {/* Mobile drawer menu */}
      {isMobileMenuOpen && (
        <>
          <div className="drawer-backdrop" onClick={() => setIsMobileMenuOpen(false)} />
          <div className={`drawer open`}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--sd-space-4)',
              borderBottom: '1px solid var(--sd-color-border-subtle)'
            }}>
              <div className="flex" style={{ gap: 'var(--sd-space-2)' }}>
                <Calendar size={20} style={{ color: 'var(--sd-color-brand-primary)' }} />
                <strong>Citizenship Countdown</strong>
              </div>
              <button
                aria-label="Close menu"
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--sd-color-border-subtle)',
                  borderRadius: 'var(--sd-radius-md)',
                  padding: '6px',
                  color: 'var(--sd-color-text-secondary)'
                }}
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: 'var(--sd-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--sd-space-2)' }}>
              {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setIsMobileMenuOpen(false); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sd-space-2)',
                      padding: 'var(--sd-space-3) var(--sd-space-4)',
                      border: '1px solid var(--sd-color-border-subtle)',
                      borderRadius: 'var(--sd-radius-md)',
                      background: isActive ? 'var(--sd-color-bg-tint-blue)' : 'var(--sd-color-bg-container)',
                      color: isActive ? 'var(--sd-color-brand-primary)' : 'var(--sd-color-text-secondary)',
                      textAlign: 'left',
                      cursor: 'pointer'
                    }}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <main className="container app-main" style={{ 
        paddingTop: activeTab === 'questionnaire' ? (isSmallScreen ? 'var(--sd-space-2)' : 'var(--sd-space-4)') : 'var(--sd-space-8)',
        paddingBottom: 'var(--sd-space-8)'
      }}>
        {/* Setup Warning */}
        {needsSetup && activeTab === 'dashboard' && (
          <div style={{
            padding: 'var(--sd-space-5)',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            border: '2px solid var(--sd-color-status-warning)',
            borderRadius: 'var(--sd-radius-lg)',
            marginBottom: 'var(--sd-space-6)',
            textAlign: 'center'
          }}>
            <FileText size={48} style={{ 
              color: 'var(--sd-color-status-warning)', 
              margin: '0 auto var(--sd-space-3) auto' 
            }} />
            <h3 style={{
              margin: '0 0 var(--sd-space-2) 0',
              fontSize: 'var(--sd-font-size-display-sm)',
              fontWeight: 'var(--sd-font-weight-bold)',
              color: 'var(--sd-color-text-primary)'
            }}>
              Complete Your Assessment
            </h3>
            <p style={{
              margin: '0 0 var(--sd-space-4) 0',
              fontSize: 'var(--sd-font-size-md)',
              color: 'var(--sd-color-text-secondary)'
            }}>
              To get accurate countdown calculations, please complete the citizenship assessment questionnaire.
            </p>
            <button 
              className="button button-primary"
              onClick={() => setActiveTab('questionnaire')}
            >
              <FileText size={16} />
              Start Assessment
            </button>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'dashboard' && eligibility && (
          <div className="grid-responsive-large">
            {/* Main countdown card - full width */}
            <div style={{ gridColumn: '1 / -1' }}>
              <CountdownTimer eligibility={eligibility} />
            </div>
            
            {/* Stats cards in a responsive grid */}
            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '36px',
                fontWeight: 'var(--sd-font-weight-bold)',
                color: 'var(--sd-color-brand-primary)',
                marginBottom: 'var(--sd-space-2)'
              }}>
                {eligibility.daysCompleted}
              </div>
              <div style={{
                fontSize: 'var(--sd-font-size-sm)',
                color: 'var(--sd-color-text-secondary)',
                fontWeight: 'var(--sd-font-weight-medium)'
              }}>
                Days Completed
              </div>
              <div style={{
                fontSize: 'var(--sd-font-size-xs)',
                color: 'var(--sd-color-text-tertiary)',
                marginTop: 'var(--sd-space-1)'
              }}>
                of {eligibility.daysRequired} required
              </div>
            </div>

            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '36px',
                fontWeight: 'var(--sd-font-weight-bold)',
                color: eligibility.daysRemaining > 0 ? 'var(--sd-color-status-warning)' : 'var(--sd-color-status-positive)',
                marginBottom: 'var(--sd-space-2)'
              }}>
                {eligibility.daysRemaining}
              </div>
              <div style={{
                fontSize: 'var(--sd-font-size-sm)',
                color: 'var(--sd-color-text-secondary)',
                fontWeight: 'var(--sd-font-weight-medium)'
              }}>
                Days Remaining
              </div>
              <div style={{
                fontSize: 'var(--sd-font-size-xs)',
                color: 'var(--sd-color-text-tertiary)',
                marginTop: 'var(--sd-space-1)'
              }}>
                {eligibility.isEligible ? 'Eligible now!' : 'Until eligible'}
              </div>
            </div>

            <div className="card" style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '36px',
                fontWeight: 'var(--sd-font-weight-bold)',
                color: 'var(--sd-color-text-primary)',
                marginBottom: 'var(--sd-space-2)'
              }}>
                {trips.length}
              </div>
              <div style={{
                fontSize: 'var(--sd-font-size-sm)',
                color: 'var(--sd-color-text-secondary)',
                fontWeight: 'var(--sd-font-weight-medium)'
              }}>
                Total Trips
              </div>
              <div style={{
                fontSize: 'var(--sd-font-size-xs)',
                color: 'var(--sd-color-text-tertiary)',
                marginTop: 'var(--sd-space-1)'
              }}>
                Tracked outside Canada
              </div>
            </div>
            
            {trips.length > 0 && (
              <div className="card span-2">
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 'var(--sd-space-4)'
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: 'var(--sd-font-size-lg)',
                    fontWeight: 'var(--sd-font-weight-semibold)',
                    color: 'var(--sd-color-text-primary)'
                  }}>
                    Recent Trips
                  </h3>
                  <button 
                    className="button button-secondary"
                    onClick={() => setActiveTab('trips')}
                    style={{ padding: 'var(--sd-space-2) var(--sd-space-3)', fontSize: 'var(--sd-font-size-sm)' }}
                  >
                    <MapPin size={14} />
                    View All
                  </button>
                </div>
                <div className="stack" style={{ gap: 'var(--sd-space-3)' }}>
                  {trips.slice(-3).map(trip => (
                    <div
                      key={trip.id}
                      style={{
                        padding: 'var(--sd-space-4)',
                        backgroundColor: 'var(--sd-color-bg-muted)',
                        borderRadius: 'var(--sd-radius-lg)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        border: '1px solid var(--sd-color-border-subtle)'
                      }}
                    >
                      <div>
                        <div style={{
                          fontSize: 'var(--sd-font-size-md)',
                          fontWeight: 'var(--sd-font-weight-semibold)',
                          color: 'var(--sd-color-text-primary)',
                          marginBottom: '2px'
                        }}>
                          {trip.destination}
                        </div>
              <div style={{
                fontSize: 'var(--sd-font-size-sm)',
                color: 'var(--sd-color-text-tertiary)'
              }}>
                {formatDateFns(parseISO(trip.departureDate), 'yyyy-MM-dd')} - {formatDateFns(parseISO(trip.returnDate), 'yyyy-MM-dd')}
              </div>
                        <div style={{
                          fontSize: 'var(--sd-font-size-xs)',
                          color: 'var(--sd-color-text-secondary)',
                          marginTop: '2px'
                        }}>
                          {(() => {
                            if (!user?.prDate) return null;
                            const withTrip = calculateCitizenshipEligibility(user.prDate, trips, temporaryPeriods);
                            const withoutTrip = calculateCitizenshipEligibility(user.prDate, trips.filter(t => t.id !== trip.id), temporaryPeriods);
                            const delta = withTrip.daysRemaining - withoutTrip.daysRemaining;
                            if (delta === 0) return 'Impact: no change';
                            const sign = delta > 0 ? '+' : '−';
                            return `Impact: ${sign}${Math.abs(delta)} day${Math.abs(delta) !== 1 ? 's' : ''} remaining`;
                          })()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 'var(--sd-space-2)', alignItems: 'center' }}>
                        {trip.accompaniedByCanadianSpouse && (
                          <div className="badge badge-positive" style={{ fontSize: 'var(--sd-font-size-xs)' }}>
                            With Spouse
                          </div>
                        )}
                        <div className={`badge ${trip.countsTowardResidency ? 'badge-positive' : 'badge-neutral'}`}>
                          {trip.countsTowardResidency ? 'Counts' : 'Doesn\'t Count'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'trips' && (
          <>
            {questionnaireResponse?.responses?.has_trips === true && !user?.email && (
              <div style={{
                marginBottom: 'var(--sd-space-4)',
                padding: 'var(--sd-space-5)',
                backgroundColor: 'var(--sd-color-bg-tint-blue)',
                border: '2px solid var(--sd-color-brand-primary)',
                borderRadius: 'var(--sd-radius-lg)',
                textAlign: 'center'
              }}>
                <h3 style={{
                  margin: '0 0 var(--sd-space-2) 0',
                  fontSize: 'var(--sd-font-size-lg)',
                  fontWeight: 'var(--sd-font-weight-semibold)',
                  color: 'var(--sd-color-text-primary)'
                }}>
                  {trips.length === 0 ? 'Add Your Trips' : 'Great! Your Trips Are Added'}
                </h3>
                <p style={{
                  margin: '0 0 var(--sd-space-4) 0',
                  fontSize: 'var(--sd-font-size-md)',
                  color: 'var(--sd-color-text-secondary)'
                }}>
                  {trips.length === 0 
                    ? 'Add your trips outside Canada to get an accurate countdown calculation' 
                    : `You've added ${trips.length} trip${trips.length === 1 ? '' : 's'}. Ready to continue?`
                  }
                </p>
                {trips.length > 0 && (
                  <button
                    className="button button-primary"
                    onClick={() => setShowEmailCapture(true)}
                    style={{ 
                      fontSize: 'var(--sd-font-size-md)',
                      padding: 'var(--sd-space-3) var(--sd-space-6)'
                    }}
                  >
                    Continue to Save Progress
                  </button>
                )}
              </div>
            )}
            <Suspense fallback={<div style={{ color: 'var(--sd-color-text-secondary)' }}>Loading…</div>}>
              <TripTracker
              trips={trips}
              onAddTrip={handleAddTrip}
              onUpdateTrip={handleUpdateTrip}
              onDeleteTrip={handleDeleteTrip}
              recentImpact={recentImpact}
              onClearRecentImpact={() => setRecentImpact(null)}
              />
            </Suspense>
          </>
        )}

        {activeTab === 'questionnaire' && (
          <Suspense fallback={<div style={{ color: 'var(--sd-color-text-secondary)' }}>Loading…</div>}>
            <Questionnaire
            responses={questionnaireResponses}
            onUpdateResponses={handleUpdateQuestionnaireResponses}
            onComplete={handleCompleteQuestionnaire}
            />
          </Suspense>
        )}

        {activeTab === 'notifications' && (
          <Suspense fallback={<div style={{ color: 'var(--sd-color-text-secondary)' }}>Loading…</div>}>
            <NotificationSettings
              preferences={notificationPreferences}
              onUpdatePreferences={handleUpdateNotificationPreferences}
              eligibilityDate={eligibility?.eligibilityDate || null}
              userEmail={user?.email}
            />
          </Suspense>
        )}
      </main>

      {/* Footer Disclaimer */}
      <footer className="app-footer" style={{
        padding: 'var(--sd-space-6) var(--sd-space-4)',
        backgroundColor: 'var(--sd-color-bg-muted)',
        borderTop: '1px solid var(--sd-color-border-subtle)',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <p style={{
            fontSize: 'var(--sd-font-size-xs)',
            color: 'var(--sd-color-text-tertiary)',
            margin: '0 0 var(--sd-space-2) 0',
            lineHeight: 1.6
          }}>
            <strong style={{ color: 'var(--sd-color-text-secondary)' }}>Privacy:</strong> We use a secure token-based authentication system. No passwords are stored. Your data is encrypted and only accessible via your unique token link. We only collect the email you provide (optional) to send countdown reminders. We never share your information with third parties.
          </p>
          <p style={{
            fontSize: 'var(--sd-font-size-xs)',
            color: 'var(--sd-color-text-tertiary)',
            margin: '0 0 var(--sd-space-2) 0',
            lineHeight: 1.6
          }}>
            <strong style={{ color: 'var(--sd-color-text-secondary)' }}>Disclaimer:</strong> This website is not affiliated with, endorsed by, or connected to Immigration, Refugees and Citizenship Canada (IRCC) or the Government of Canada. This is an independent tool created to help permanent residents track their citizenship eligibility. All calculations are based on publicly available information and should not be considered official legal advice. For official information, please visit{' '}
            <a 
              href="https://www.canada.ca/en/immigration-refugees-citizenship.html" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: 'var(--sd-color-brand-primary)',
                textDecoration: 'none'
              }}
            >
              canada.ca/citizenship
            </a>.
          </p>
          <p style={{
            fontSize: 'var(--sd-font-size-xs)',
            color: 'var(--sd-color-text-tertiary)',
            margin: 0
          }}>
            © {new Date().getFullYear()} Citizenship Countdown. Made with{' '}
            <MapleLeaf size={12} color="var(--sd-color-brand-primary)" style={{ verticalAlign: 'middle', margin: '0 2px' }} />
            {' '}for Canadian permanent residents.
          </p>
        </div>
      </footer>

      {questionnaireResponse && (
        <a
          className="fab"
          href="https://buymeacoffee.com/shaddyb"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            borderRadius: '9999px',
            background: 'var(--sd-color-bg-container)',
            border: '1px solid var(--sd-color-border-subtle)',
            color: 'var(--sd-color-text-secondary)',
            boxShadow: 'var(--sd-shadow-sm)'
          }}
        >
          <Coffee size={16} aria-hidden="true" />
          <span style={{ fontWeight: 600 }}>Buy me a coffee</span>
        </a>
      )}
    </div>
  );
}

export default App;
